import {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  createContext,
  useContext,
} from "react";
import { Button, Container, Row, Col, Form } from "react-bootstrap";
import {
  BuildHtmlElement,
  BuildingSelect,
  DateInput,
} from "../../components/forms/formElements";
import { useMobileMode } from "../../utils/useMobile";
import InspectionProtocolFiles from "./InspectionProtocolFiles";
import { useDataProvider } from "../../providers/offline_data_provider";

const paginationContext = createContext("paginationContext");

export function usePaginationContext() {
  return useContext(paginationContext);
}

function ProvidePaginationContext({ value, children }) {
  return (
    <paginationContext.Provider value={value}>
      {children}
    </paginationContext.Provider>
  );
}

const DEFAULT_ARRIVAL_PURPOSE_OPTIONS = [
  "Przegląd garażu",
  "Przegląd napowietrzanie",
  "Przegląd rozdzielnic",
  "Regulacje",
  "Awaria",
  "Zgłoszenie",
  "Kalibracja",
  "Przegląd detekcji",
  "Przegląd central wentylacyjnych",
  "Asysta",
];

function getArrivalPurposeOptions(settingsData) {
  const raw = settingsData?.protocol_arrival_signature;

  let parsed = [];
  if (Array.isArray(raw)) {
    parsed = raw;
  } else if (typeof raw === "string") {
    try {
      const json = JSON.parse(raw);
      if (Array.isArray(json)) parsed = json;
    } catch (e) {
      parsed = [];
    }
  }

  const normalized = parsed
    .filter((v) => typeof v === "string")
    .map((v) => v.trim())
    .filter((v) => v.length > 0);

  return normalized.length > 0 ? normalized : DEFAULT_ARRIVAL_PURPOSE_OPTIONS;
}

const ProtocolFormPagination = ({
  formData,
  companies,
  handleSelectLeadingCompany,
  handleSetBuilding,
  handleSetArrivalPurpose,
  handleSetProtocolDate,
  handleSetFile,
  handleSave,
}) => {
  const { settingsData } = useDataProvider();
  const isStandalone = useMobileMode();
  const [currentPage, setCurrentPage] = useState(0);
  // Each control list (Ul with control=true) registers its own gate by a stable
  // id and reports whether it currently allows moving to the next page. Only the
  // elements of the current page are mounted, so `gates` only ever holds gates
  // for the visible page; unmounted lists release their gate. The "Next" button
  // is enabled only when every registered gate allows the move.
  const [gates, setGates] = useState({});

  const arrivalPurposeOptions = useMemo(
    () => getArrivalPurposeOptions(settingsData),
    [settingsData],
  );

  // Split elements into page groups based on pageBreak
  const pages = useMemo(() => {
    const grouped = [[]]; // page 0
    for (const el of formData.elements) {
      grouped[grouped.length - 1].push(el); // always push current element

      if (el.pageBreak === true && isStandalone) {
        grouped.push([]); // start a new page for the next element
      }
    }

    // Remove empty last page if it was added due to last item having pageBreak
    if (grouped[grouped.length - 1].length === 0) {
      grouped.pop();
    }

    return grouped;
  }, [formData.elements, isStandalone]);

  const totalPages = pages.length; // +1 for ProtocolFormSection

  const goNext = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1));
  const goPrevious = () => setCurrentPage((prev) => Math.max(prev - 1, 0));

  // po zmianie strony wracamy na górę (pomijamy pierwsze wyrenderowanie)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    // .body ma overflow:auto - może być właściwym kontenerem przewijania
    document
      .querySelector(".body")
      ?.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [currentPage]);

  const registerPageGate = useCallback(
    (gateId, allowMove) => {
      if (!isStandalone) return;
      setGates((prev) =>
        prev[gateId] === allowMove ? prev : { ...prev, [gateId]: allowMove }
      );
    },
    [isStandalone]
  );

  const releasePageGate = useCallback((gateId) => {
    setGates((prev) => {
      if (!(gateId in prev)) return prev;
      const { [gateId]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const paginationValue = useMemo(
    () => ({ registerPageGate, releasePageGate }),
    [registerPageGate, releasePageGate]
  );

  // Move is allowed only when every gate on the (currently mounted) page allows it.
  const canMoveNext = Object.values(gates).every(Boolean);

  return (
    <Container fluid className="protocol-form my-4">
      <ProvidePaginationContext value={paginationValue}>
        <Row>
          <Col>
            {currentPage === 0 && (
              <ProtocolFormSection
                formData={formData}
                companies={companies}
                handleSelectLeadingCompany={handleSelectLeadingCompany}
                handleSetBuilding={handleSetBuilding}
                handleSetArrivalPurpose={handleSetArrivalPurpose}
                handleSetProtocolDate={handleSetProtocolDate}
                handleSetFile={handleSetFile}
                arrivalPurposeOptions={arrivalPurposeOptions}
              />
            )}

            {pages[currentPage].map((element, index) => (
              <BuildHtmlElement key={index} element={element} />
            ))}
          </Col>
        </Row>
      </ProvidePaginationContext>

      {isStandalone && (
        <Row className="mt-4">
          <Col className="d-flex justify-content-between">
            <Button
              variant="secondary"
              onClick={goPrevious}
              disabled={currentPage === 0}
            >
              Poprzednia
            </Button>
            <span className="align-self-center">
              Strona {currentPage + 1} z {totalPages}
            </span>
            {currentPage === totalPages - 1 ? (
              <Button
                variant="success"
                onClick={() => {
                  handleSave();
                }}
              >
                Zapisz i zakończ
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={goNext}
                disabled={!canMoveNext}
              >
                Następna
              </Button>
            )}
          </Col>
        </Row>
      )}
    </Container>
  );
};

function ProtocolFormSection({
  handleSetBuilding,
  handleSetArrivalPurpose,
  handleSetProtocolDate,
  formData,
  handleSetFile,
  arrivalPurposeOptions,
  companies,
  handleSelectLeadingCompany,
}) {
  const buildingIsSet =
    formData.building !== null && formData.building?.building_id != undefined;

  return (
    <div className={"protocol_section"}>
      <h4 className={"w-100 text-center"}>OBIEKT I DATA</h4>

      <Form.Group className={"m-2"} controlId={"leading_company"}>
        <Form.Label>Spółka obsługująca</Form.Label>
        <Form.Select
          value={formData.leading_company_id ?? ""}
          onChange={handleSelectLeadingCompany}
        >
          <option value={""}>Wybierz spółkę...</option>
          {(companies || []).map((company) => (
            <option key={company.id} value={company.id}>
              {company.full_name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <BuildingSelect
        onChange={handleSetBuilding}
        building={formData.building}
      />
      <DateInput
        value={{
          name: "protocol_date",
          class: "",
          label: "Data protokołu",
          value: formData.date,
        }}
        onChange={handleSetProtocolDate}
      />
      <div className={`m-2 `}>
        <Form.Group className="mb-3">
          <Form.Label>Cel przyjazdu</Form.Label>
          <Form.Select
            aria-label="Cel przyjazdu"
            onChange={handleSetArrivalPurpose}
            defaultValue={formData.arrival_purpose}
          >
            <option></option>
            {arrivalPurposeOptions.map((a, i) => {
              return (
                <option key={i} value={a}>
                  {a}
                </option>
              );
            })}
          </Form.Select>
        </Form.Group>
      </div>
      {formData.type !== "default" && buildingIsSet && (
        <InspectionProtocolFiles
          type={formData.type}
          onFileChange={handleSetFile}
        />
      )}
    </div>
  );
}

export default ProtocolFormPagination;
